import React, { useEffect, useState, type ReactNode } from 'react'
import { EditorView } from 'prosemirror-view'
import type { Schema } from 'prosemirror-model'
import type { EditorState, Transaction } from 'prosemirror-state'
import { toggleMark } from 'prosemirror-commands'
import { wrapInList } from 'prosemirror-schema-list'
import { Bold, Italic, List, OrderedList, SyncProgress, Success } from '@/assets/icons'
import TextSizeDropdown from './TextSizeDropdown'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import './text-size-dropdown.css'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'

// Enums for better type safety and maintainability
export enum EditorSaveStatusType {
  IDLE = 'idle',
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
  CONNECTION_LOST = 'connection-lost',
}

export enum FormatProperty {
  STRONG = 'strongActive',
  EM = 'emActive',
  BULLET_LIST = 'bulletListActive',
  ORDERED_LIST = 'orderedListActive',
}

export enum ButtonTitle {
  BOLD = 'Bold',
  ITALIC = 'Italic',
  BULLET_LIST = 'Bullet List',
  NUMBERED_LIST = 'Numbered List',
}

interface MarkdownEditorToolbarProps {
  editorView: EditorView | null
  schema: Schema
  saveStatus?: EditorSaveStatusType
  isApplyingEdit?: boolean
  onRetry?: () => void
  currentTextStyle?: string
  formatState?: {
    [FormatProperty.STRONG]: boolean
    [FormatProperty.EM]: boolean
    [FormatProperty.BULLET_LIST]: boolean
    [FormatProperty.ORDERED_LIST]: boolean
  }
  artifactId?: string
  artifactVersionId?: string
  conversationId?: string
  sessionId?: string
  matterId?: string
  onFormatAction?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackEvent?: <T extends AnalyticsEvent>(event: T, properties?: any) => void
  templateButton?: ReactNode
}

interface MenuButtonProps {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}

const MenuButton: React.FC<MenuButtonProps> = ({ onClick, active, disabled, title, children }) => {
  return (
    <div className="relative group">
      {/* Outer container - 28x28px */}
      <div className="w-7 h-7 flex items-center justify-center p-0 relative rounded-md">
        <button
          className={`w-full h-full flex items-center justify-center p-1 relative shrink-0 transition-colors ${
            active
              ? 'bg-brand-neutral-300 text-brand-neutral-950 rounded-md'
              : 'bg-transparent text-brand-neutral-950 rounded-full hover:bg-brand-neutral-300 hover:rounded-md'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={onClick}
          disabled={disabled}
          title={title}
          aria-label={title}
          aria-pressed={active}
          type="button"
        >
          <div className="w-4 h-4 flex items-center justify-center overflow-hidden relative shrink-0">{children}</div>
        </button>
      </div>

      {/* Tooltip */}
      <div className="absolute left-1/2 top-8 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-brand-neutral-500 text-white text-2xs font-medium px-1 py-0.5 rounded-sm whitespace-nowrap z-10 pointer-events-none">
        {title}
      </div>
    </div>
  )
}

const SaveStatus: React.FC<{ status: EditorSaveStatusType; onRetry?: () => void }> = ({ status, onRetry }) => {
  return (
    <div className="flex items-center gap-1 text-xs font-normal text-brand-neutral-600" role="status" aria-live="polite">
      {status === EditorSaveStatusType.SAVING && (
        <>
          <span>Saving</span>
          <SyncProgress className="w-3 h-3 text-brand-neutral-600" />
        </>
      )}
      {status === EditorSaveStatusType.SAVED && (
        <>
          <span>Saved</span>
          <Success className="w-3 h-3 text-brand-neutral-600" />
        </>
      )}
      {status === EditorSaveStatusType.ERROR && (
        <>
          <span>Auto-save failed. </span>
          <button onClick={onRetry} className="underline" aria-label="Saving failed, click to retry">
            Retry
          </button>
        </>
      )}
      {status === EditorSaveStatusType.CONNECTION_LOST && (
        <>
          <span>Connection lost</span>
        </>
      )}
    </div>
  )
}

export default function MarkdownEditorToolbar({
  editorView,
  schema,
  saveStatus = EditorSaveStatusType.IDLE,
  isApplyingEdit = false,
  onRetry,
  currentTextStyle,
  formatState,
  artifactId,
  artifactVersionId,
  conversationId,
  sessionId,
  matterId,
  onFormatAction,
  trackEvent,
  templateButton,
}: MarkdownEditorToolbarProps) {
  const [menuState, setMenuState] = useState({
    [FormatProperty.STRONG]: false,
    [FormatProperty.EM]: false,
    [FormatProperty.BULLET_LIST]: false,
    [FormatProperty.ORDERED_LIST]: false,
  })

  // Use real-time format state if provided, otherwise fall back to local state
  const activeFormatState = formatState || menuState

  // Only use local state updates when formatState is not provided (backward compatibility)
  useEffect(() => {
    if (!editorView || formatState) return // Skip if we have real-time formatState

    const updateMenuState = () => {
      const state = editorView.state
      const { from, to } = state.selection

      setMenuState({
        [FormatProperty.STRONG]: state.doc.rangeHasMark(from, to, schema.marks.strong),
        [FormatProperty.EM]: state.doc.rangeHasMark(from, to, schema.marks.em),
        [FormatProperty.BULLET_LIST]: false,
        [FormatProperty.ORDERED_LIST]: false,
      })
    }

    // Initial update
    updateMenuState()

    // Listen for state changes
    const updateListener = () => updateMenuState()
    editorView.dom.addEventListener('focus', updateListener)
    editorView.dom.addEventListener('blur', updateListener)

    return () => {
      editorView.dom.removeEventListener('focus', updateListener)
      editorView.dom.removeEventListener('blur', updateListener)
    }
  }, [editorView, schema, formatState])

  const handleCommand = (
    command: (state: EditorState, dispatch?: (tr: Transaction) => void) => boolean,
    formatType?: 'bold' | 'italic' | 'bulletList' | 'orderedList'
  ) => {
    if (!editorView) return
    command(editorView.state, editorView.dispatch)
    editorView.focus()

    // Track formatting action for analytics
    if (artifactId && trackEvent && formatType && artifactVersionId && conversationId && sessionId) {
      trackEvent(AnalyticsEvent.ArtifactEditorFormattingUsed, {
        formatType,
        artifactId,
        artifactVersionId,
        conversationId,
        sessionId,
        matterId: matterId ?? 'none',
      })
    }

    // Call onFormatAction to increment counter
    if (onFormatAction) {
      onFormatAction()
    }
  }

  if (!editorView) return null

  return (
    <div className="bg-white relative w-full sticky top-0 z-10">
      {/* Border bottom */}
      <div className="absolute border-brand-neutral-200 border-b border-solid bottom-0 left-0 right-0 pointer-events-none" />

      {/* Main content */}
      <div className="relative w-full">
        <div className="box-border items-start justify-start py-2 relative w-full">
          {/* Buttons container - 28px tall, no padding */}
          <div className="flex flex-row justify-between box-border items-center px-2 h-7 [container-type:inline-size]">
            {/* Left side - Fixed width controls */}
            <div className="flex flex-row gap-1 items-center flex-none">
              {/* Text size dropdown */}
              <div className="box-border flex flex-col items-start justify-start p-0 relative">
                <TextSizeDropdown
                  editorView={editorView}
                  schema={schema}
                  currentTextStyle={currentTextStyle}
                  artifactId={artifactId}
                  matterId={matterId}
                  trackEvent={trackEvent}
                />
              </div>

              {/* Separator */}
              <div className="flex flex-row items-center self-stretch">
                <div className="box-border flex flex-row gap-2.5 h-full items-center justify-center p-0 relative shrink-0 w-2">
                  <div className="flex h-0 items-center justify-center relative shrink-0 w-0">
                    <div className="flex-none rotate-90">
                      <div className="h-0 relative w-7">
                        <div className="absolute bottom-0 left-0 right-0 top-0 h-px bg-brand-neutral-200" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Format buttons */}
              <MenuButton
                onClick={() => handleCommand(toggleMark(schema.marks.strong), 'bold')}
                active={activeFormatState[FormatProperty.STRONG]}
                title={ButtonTitle.BOLD}
              >
                <Bold className="w-full h-full" />
              </MenuButton>

              <MenuButton
                onClick={() => handleCommand(toggleMark(schema.marks.em), 'italic')}
                active={activeFormatState[FormatProperty.EM]}
                title={ButtonTitle.ITALIC}
              >
                <Italic className="w-full h-full" />
              </MenuButton>

              {/* Separator */}
              <div className="flex flex-row items-center self-stretch">
                <div className="box-border flex flex-row gap-2.5 h-full items-center justify-center p-0 relative shrink-0 w-2">
                  <div className="flex h-0 items-center justify-center relative shrink-0 w-0">
                    <div className="flex-none rotate-90">
                      <div className="h-0 relative w-7">
                        <div className="absolute bottom-0 left-0 right-0 top-0 h-px bg-brand-neutral-200" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* List buttons */}
              <MenuButton
                onClick={() => handleCommand(wrapInList(schema.nodes.bullet_list), 'bulletList')}
                active={activeFormatState[FormatProperty.BULLET_LIST]}
                title={ButtonTitle.BULLET_LIST}
              >
                <List className="w-full h-full" />
              </MenuButton>

              <MenuButton
                onClick={() => handleCommand(wrapInList(schema.nodes.ordered_list), 'orderedList')}
                active={activeFormatState[FormatProperty.ORDERED_LIST]}
                title={ButtonTitle.NUMBERED_LIST}
              >
                <OrderedList className="w-full h-full" />
              </MenuButton>
            </div>

            {/* Right side - status indicators + trailing toolbar slot */}
            <div className="flex items-center gap-3">
              {isApplyingEdit ? (
                <div className="flex items-center gap-1 text-xs font-normal text-brand-neutral-600" role="status">
                  <CircularProgressContinuousSized size={14} thickness={5} hexColor="#76736A" />
                </div>
              ) : (
                <SaveStatus status={saveStatus} onRetry={onRetry} />
              )}
              {templateButton}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
